import { createTheme, SxProps, Theme, ThemeProvider } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { ReactComponent as ExclamationIcon } from 'assets/icons/exclamation_mark.svg';
import { ReactComponent as CheckIcon } from 'assets/icons/check_mark.svg';
import { ReactComponent as InformationIcon } from 'assets/icons/information_mark.svg';
import { ReactComponent as WarningIcon } from 'assets/icons/warning_mark.svg';
import { IconButton, Typography } from '@mui/material';
import { CrossSimpleIcon } from 'components/forms/ConfirmationModal/ConfirmationModal.styled';
import styled from '@emotion/styled';

const theme = createTheme({
    typography: {
        fontFamily: ['Assistant', 'sans-serif'].join(','),
        fontSize: 15,
        body1: {
            fontSize: '15px',
            fontWeight: 400,
            lineHeight: '20px',
            color: '#282828',
        },
    },
    palette: {
        warning: {
            main: '#FFE7101A !important',
        },
    },
});

export const CloseIcon = styled(CrossSimpleIcon)<{ color?: string }>`
    cursor: pointer;
    path {
        fill: ${({ color }) => color || 'gray'};
    }
    width: 9px;
    height: 9px;
`;

export interface AlertObj {
    type: 'success' | 'warning' | 'error' | 'info';
    value: string;
}

interface Alertprops {
    type: 'success' | 'warning' | 'error' | 'info';
    text: string;
    width?: number;
    sx?: SxProps<Theme>;
    open: boolean;
    onClose: () => void;
}

export default function AlertMessage({ type, text, width = 426, sx, open, onClose }: Alertprops) {
    const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        onClose();
    };

    const styles = {
        '&.MuiAlert-standardSuccess': {
            backgroundColor: '#E5FAE6',
        },
        '&.MuiAlert-standardInfo': {
            bgcolor: '#F3F1FF',
        },
        '&.MuiAlert-standardWarning': {
            bgcolor: '#FFE7101A',
        },
        '&.MuiAlert-standardError': {
            bgcolor: '#FFCFCF',
        },
        width: width,
        ...sx,
    };

    const typeColors: Record<Alertprops['type'], string> = {
        success: '#00CB14',
        info: '#4A52BB',
        warning: '#686868',
        error: '#FF2727',
    };

    return (
        <Snackbar
            open={open}
            onClose={handleClose}
            // autoHideDuration={5000}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
            <Alert
                severity={type}
                sx={styles}
                iconMapping={{
                    info: <InformationIcon fill="#4A52BB" />,
                    success: <CheckIcon fill="#00CB14" />,
                    warning: <ExclamationIcon fill="#686868" />,
                    error: <WarningIcon fill="#FF2727" />,
                }}
                action={
                    <IconButton
                        aria-label="close"
                        size="small"
                        sx={{ color: typeColors[type] }}
                        onClick={() => onClose()}
                    >
                        <CloseIcon fontSize="inherit" color={typeColors[type]} />
                    </IconButton>
                }
            >
                <ThemeProvider theme={theme}>
                    <Typography> {text}</Typography>
                </ThemeProvider>
            </Alert>
        </Snackbar>
    );
}
